import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  excludeRemaining,
  getBatchCarryover,
  getBatchNeedsAttention,
  getBatchRunProducts,
  getBatchRuns,
  getProductAttempts,
  startBatchRetry,
  undoExclusion,
} from '../services/batch-retry.service';
import type {
  BatchExclusionRequest,
  BatchRetryRequest,
  BatchRunProductsQuery,
  BatchRuns,
} from '../types/batch-retry.api.types';

/** Poll cadence while the batch is moving (paused batches still drain in-flight work). */
export const BATCH_RUNS_POLL_MS = 20_000;

/** What changes when a retry starts, finishes, is paused or is excluded. */
export function batchRunsSignature(runs: BatchRuns | undefined): string | null {
  if (!runs) return null;
  const perRun = runs.runs.map((r) => `${r.runId}:${r.status ?? ''}`).join(',');
  return [
    runs.activeRetryRunId ?? '',
    runs.batchStatus ?? '',
    String(runs.attemptsUsed),
    perRun,
  ].join('|');
}

/**
 * The runs poll stops once the batch settles, and nothing else refetches the header, funnel,
 * coverage or grid. When the runs read transitions (retry finished, status or attempts changed),
 * invalidate everything under `batches` (job-keyed detail, workflow-keyed coverage/insights and the
 * grid list) plus the tab counts. The first read is not a transition.
 */
export function useInvalidateBatchOnRunsTransition(runs: BatchRuns | undefined): void {
  const queryClient = useQueryClient();
  const previous = useRef<string | null>(null);
  const signature = batchRunsSignature(runs);
  useEffect(() => {
    if (signature === null) return;
    if (previous.current !== null && previous.current !== signature) {
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.catalogOperations.all, 'batches'],
      });
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.catalogOperations.all, 'status-counts'],
      });
    }
    previous.current = signature;
  }, [signature, queryClient]);
}

/** Page size for a retry run's products table. */
export const BATCH_RUN_PRODUCTS_PAGE_SIZE = 50;

/** True while something is still running or draining. */
export function isBatchLive(runs: BatchRuns | undefined): boolean {
  const s = runs?.batchStatus;
  return s === 'IN_PROGRESS' || s === 'RETRYING' || s === 'DELIVERING' || s === 'PAUSED';
}

/** The batch as root run + retries; polls while live. */
export function useBatchRuns(jobId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchRuns(jobId),
    queryFn: () => getBatchRuns(jobId),
    enabled: enabled && Boolean(jobId),
    staleTime: 0,
    refetchInterval: (query) => (isBatchLive(query.state.data) ? BATCH_RUNS_POLL_MS : false),
  });
}

/**
 * Carry-over between batches. Null data = nothing to show (older backend, 404): the UI renders
 * nothing new and never an error. Lives under `batches`/jobId, so a retry/runs transition refreshes it.
 */
export function useBatchCarryover(jobId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchCarryover(jobId),
    queryFn: () => getBatchCarryover(jobId),
    enabled: enabled && Boolean(jobId),
    staleTime: 30_000,
    retry: false,
  });
}

/** One retry run's products. */
export function useBatchRunProducts(
  jobId: string,
  runId: string | null,
  query: BatchRunProductsQuery,
  live = false,
) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchRunProducts(jobId, runId ?? '', { ...query }),
    queryFn: () => getBatchRunProducts(jobId, runId ?? '', query),
    enabled: Boolean(jobId) && Boolean(runId),
    placeholderData: keepPreviousData,
    // A running retry's products change as it works; finished runs never do.
    refetchInterval: live ? BATCH_RUNS_POLL_MS : false,
  });
}

/** Every attempt at one product (root, then each retry). */
export function useProductAttempts(jobId: string, productId: string | null) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchProductAttempts(jobId, productId ?? ''),
    queryFn: () => getProductAttempts(jobId, productId ?? ''),
    enabled: Boolean(jobId) && Boolean(productId),
  });
}

/** Classified Needs Attention; polls with the runs while the batch is moving. */
export function useBatchNeedsAttention(jobId: string, enabled = true, live = false) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchNeedsAttention(jobId),
    queryFn: () => getBatchNeedsAttention(jobId),
    enabled: enabled && Boolean(jobId),
    staleTime: 0,
    refetchInterval: live ? BATCH_RUNS_POLL_MS : false,
  });
}

/**
 * Start a retry child. On success the whole batch page refetches (runs, header,
 * needs-attention, funnel all live under `batches`/jobId). A 409 is shown as-is:
 * the server message says why (running, paused, out of attempts, nothing to retry).
 */
export function useStartBatchRetry(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BatchRetryRequest) => startBatchRetry(jobId, body),
    onSuccess: (res) => {
      toast.success(
        res.replayed
          ? `Retry #${String(res.attempt)} is already running`
          : `Retry #${String(res.attempt)} started for ${res.estimatedProducts.toLocaleString()} products`,
      );
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.catalogOperations.all, 'batches', jobId],
      });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Could not start the retry'));
    },
  });
}

/** Exclude what is left; refetches the whole batch page like a retry does. */
export function useExcludeRemaining(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BatchExclusionRequest) => excludeRemaining(jobId, body),
    onSuccess: (res) => {
      toast.success(
        `${res.count.toLocaleString()} product${res.count === 1 ? '' : 's'} excluded from this batch`,
      );
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.catalogOperations.all, 'batches', jobId],
      });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Could not exclude the products'));
    },
  });
}

/** Undo the exclusion. */
export function useUndoExclusion(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => undoExclusion(jobId),
    onSuccess: (res) => {
      toast.success(`${res.restored.toLocaleString()} products are back in Needs Attention`);
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.catalogOperations.all, 'batches', jobId],
      });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Could not undo the exclusion'));
    },
  });
}
